import { FC, ReactNode, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { KidTemplateType } from '../../../../../api/kids/types';
import type { FormatOptions } from '../../../raidr/kidsV2/types/KidPdfGeneratorV2.types';
import {
  btnPrimary,
  btnSecondary,
  formDropdownFullBleedClassName,
  formatCreateTemplateSubtitle,
  formatKidTemplateTypeLabel,
  getTemplateReferenceFieldLabel,
  inputClassName,
  inputErrorClassName,
  sortTemplateTypesByRank,
} from '../templateManagement.constants';
import { TemplateReferenceOptions } from '../templateManagement.types';
import { cn } from '../../../../../utilities/cn';
import TemplateContentEditor from './TemplateContentEditor';
import TemplateReferenceField from './TemplateReferenceField';
import TemplateFormDropdown from './TemplateFormDropdown';
import TemplateTypeChip from './TemplateTypeChip';
import {
  formatOptionsForPersistence,
  getInitialTemplateEditorFormatOptions,
} from '../templateManagement.utils';
import { useCreateKidTemplate } from '../../../../../api/kids/hooks/mutations';
import {
  extractDetail,
  isConflictError,
} from '../../../../../utilities/QueryHelpers';

type CreateTemplateFormValues = {
  name: string;
  type: KidTemplateType;
  referenceId: string;
  content: string;
  formatOptions: FormatOptions;
};

type CreateTemplatePanelProps = TemplateReferenceOptions & {
  clientId: string;
  documentLanguage: string;
  onCancel: () => void;
  onCreated?: (templateName: string) => void;
};

const FormField: FC<{
  label: string;
  children: ReactNode;
  className?: string;
  hint?: string;
}> = ({ label, children, className, hint }) => (
  <div className={cn('flex min-w-0 flex-col gap-1', className)}>
    <label className="text-xs font-medium text-gray-700">{label}</label>
    {children}
    {hint ? <p className="text-xs leading-snug text-gray-500">{hint}</p> : null}
  </div>
);

const CreateTemplatePanel: FC<CreateTemplatePanelProps> = ({
  clientId,
  documentLanguage,
  onCancel,
  onCreated,
  ...referenceOptions
}) => {
  const createTemplateMutation = useCreateKidTemplate(clientId);

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<CreateTemplateFormValues>({
    defaultValues: {
      name: '',
      type: KidTemplateType.MANCO,
      referenceId: '',
      content: '',
      formatOptions: getInitialTemplateEditorFormatOptions(),
    },
    mode: 'onChange',
  });

  const templateType = watch('type');
  const name = watch('name');
  const referenceId = watch('referenceId');

  const templateTypeOptions = useMemo(
    () =>
      sortTemplateTypesByRank().map((type) => ({
        id: type,
        label: formatKidTemplateTypeLabel(type),
      })),
    [],
  );

  useEffect(() => {
    setValue('referenceId', '');
  }, [templateType, setValue]);

  const {
    onBlur: onNameBlur,
    onChange: onNameChange,
    ...nameField
  } = register('name', {
    validate: (value) =>
      value.trim().length > 0 || 'Please enter a template name.',
    setValueAs: (value) => (typeof value === 'string' ? value.trim() : value),
  });

  const handleCreate = (values: CreateTemplateFormValues) => {
    const persistedFormatOptions = formatOptionsForPersistence(values.formatOptions);

    createTemplateMutation.mutate(
      {
        template_name: values.name,
        template_type: values.type,
        template_content: values.content,
        ...(persistedFormatOptions
          ? { format_options: persistedFormatOptions }
          : {}),
        ...(values.type !== KidTemplateType.MANCO && values.referenceId
          ? { reference_id: values.referenceId }
          : {}),
        document_language: documentLanguage,
      },
      {
        onSuccess: () => onCreated?.(values.name),
        onError: (error) => {
          if (!isConflictError(error)) return;

          setError('name', {
            type: 'server',
            message:
              extractDetail(error) ??
              'A template with this name already exists.',
          });
        },
      },
    );
  };

  const isCreateDisabled =
    createTemplateMutation.isPending ||
    !clientId ||
    !name.trim() ||
    (templateType !== KidTemplateType.MANCO && !referenceId);

  const showReferenceField = templateType !== KidTemplateType.MANCO;

  return (
    <form
      className="flex max-h-[min(70vh,40rem)] min-h-0 flex-col"
      onSubmit={handleSubmit(handleCreate)}
    >
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
        <section className="rounded-lg border border-gray-200 bg-gray-50/70 p-3">
          <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
            <h3 className="text-sm font-semibold text-gray-900">Template scope</h3>
            <span className="text-gray-300" aria-hidden>
              ·
            </span>
            <p className="text-xs leading-snug text-gray-500">
              Choose what this template applies to before adding content.
            </p>
          </div>
          <div className={`mt-3 ${showReferenceField ? 'grid grid-cols-1 gap-3 md:grid-cols-2' : ''}`}>
            <FormField label="Type">
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <TemplateFormDropdown
                    options={templateTypeOptions}
                    value={field.value}
                    onChange={(value) => field.onChange(value as KidTemplateType)}
                    placeholder="Select type"
                    widthClassName={formDropdownFullBleedClassName}
                    renderOption={(option) => (
                      <TemplateTypeChip
                        templateType={option.id}
                        size="sm"
                        tooltip={formatCreateTemplateSubtitle(
                          option.id as KidTemplateType,
                        )}
                      />
                    )}
                    renderLabel={(option) =>
                      option ? (
                        <TemplateTypeChip
                          templateType={option.id}
                          size="sm"
                          tooltip={formatCreateTemplateSubtitle(
                            option.id as KidTemplateType,
                          )}
                        />
                      ) : (
                        'Select type'
                      )
                    }
                  />
                )}
              />
            </FormField>
            {showReferenceField ? (
              <FormField label={getTemplateReferenceFieldLabel(templateType)}>
                <Controller
                  name="referenceId"
                  control={control}
                  render={({ field }) => (
                    <TemplateReferenceField
                      templateType={templateType}
                      value={field.value}
                      onChange={field.onChange}
                      controlOnly
                      dropdownWidthClassName={formDropdownFullBleedClassName}
                      {...referenceOptions}
                    />
                  )}
                />
              </FormField>
            ) : null}
          </div>
        </section>

        <FormField label="Template name">
          <input
            {...nameField}
            onChange={(event) => {
              if (errors.name?.type === 'server') {
                clearErrors('name');
              }
              onNameChange(event);
            }}
            onBlur={(event) => {
              onNameBlur(event);
              const trimmed = event.target.value.trim();
              if (trimmed !== event.target.value) {
                setValue('name', trimmed, { shouldValidate: true });
              }
            }}
            placeholder="Template name"
            className={cn(inputClassName, errors.name && inputErrorClassName)}
          />
          {errors.name?.message ? (
            <p className="text-xs text-red-600">{errors.name.message}</p>
          ) : null}
        </FormField>

        <div>
          <p className="mb-1 text-xs font-medium text-gray-700">Content</p>
          <Controller
            name="content"
            control={control}
            render={({ field }) => (
              <TemplateContentEditor
                value={field.value}
                onChange={field.onChange}
                formatOptions={watch('formatOptions')}
                onFormatOptionsChange={(value) =>
                  setValue('formatOptions', value, { shouldDirty: true })
                }
                minHeightClass="min-h-[10rem]"
              />
            )}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-200 pt-3">
        <button
          type="submit"
          className={btnPrimary}
          disabled={isCreateDisabled}
        >
          {createTemplateMutation.isPending ? 'Creating…' : 'Create template'}
        </button>
        <button
          type="button"
          className={btnSecondary}
          onClick={onCancel}
          disabled={createTemplateMutation.isPending}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default CreateTemplatePanel;
